// React
import PropTypes from "prop-types";

// Utils
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";

const style = {
  position: "absolute",
  top: "40%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "30%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  alignContent: "center",
  button: {
    boxSizing: "border-box",
    transition: "background-color .1s ease-out",
    borderRadius: "3.01px",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "14px",
    fontVariant: "normal",
    fontWeight: "400",
    backgroundImage: "none",
    display: "inline-block",
    height: "2.14285714em",
    lineHeight: "1.42857143em",
    border: "1px solid transparent",
    margin: 0,
    padding: "4px 10px",
    verticalAlign: "baseline",
    whiteSpace: "nowrap",
    textTransform: "none",
  },
  confirm: {
    backgroundColor: "#d32f2f",
    color: "#fff",
    "&:hover": {
      transitionDuration: "0s, 0.15s",
      color: "#FFFFFF !important",
      background: "#b71c1c",
      textDecoration: "inherit",
    },
  },
  cancel: {
    color: "#000",
    marginRight: "10px",
    "&:hover": {
      transitionDuration: "0s, 0.15s",
      color: "#8993A4 !important",
    },
  },
};

function ModalDefault({
  open,
  children,
  onClickClose,
  onClickConfirm,
  confirmButtonText,
  width,
  top,
  pr,
  pl,
  closeText,
  customStyles,
}) {
  const { t } = useTranslation(["common"]);
  const modalStyle = {
    ...style,
    width: width || style.width,
    top: top || style.top,
    pr: pr || style.p,
    pl: pl || style.p,
    ...customStyles,
  };

  // Fallback to i18n if no explicit text is passed
  const confirmLabel = confirmButtonText ?? t("confirm");
  const closeLabel = closeText ?? t("cancel");

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onClickConfirm();
        }}
      >
        <Box sx={modalStyle}>
          {children}
          <Grid container direction="row-reverse" item xs={12}>
            {onClickConfirm && (
              <Button sx={{ ...style.button, ...style.confirm }} type="submit">
                {confirmLabel}
              </Button>
            )}
            {onClickClose && (
              <Button sx={{ ...style.button, ...style.cancel }} onClick={onClickClose}>
                {closeLabel}
              </Button>
            )}
          </Grid>
        </Box>
      </form>
    </Modal>
  );
}

ModalDefault.defaultProps = {
  confirmButtonText: undefined,
  width: "30%",
  top: "40%",
  pr: 4,
  pl: 4,
  closeText: undefined,
  onClickConfirm: undefined,
  onClickClose: undefined,
  customStyles: {},
};

ModalDefault.propTypes = {
  open: PropTypes.bool.isRequired,
  children: PropTypes.element.isRequired,
  onClickClose: PropTypes.func,
  onClickConfirm: PropTypes.func,
  confirmButtonText: PropTypes.string,
  width: PropTypes.string,
  top: PropTypes.string,
  pr: PropTypes.number,
  pl: PropTypes.number,
  closeText: PropTypes.string,
  customStyles: PropTypes.shape({
    width: PropTypes.string,
    top: PropTypes.string,
    p: PropTypes.number,
    bgcolor: PropTypes.string,
    boxShadow: PropTypes.number,
    borderRadius: PropTypes.number,
    // You can add more fields as needed or leave it flexible
  }),
};

export default ModalDefault;
